<template>
  <div class="flex min-h-full flex-1 flex-col justify-center px-6 pt-24 sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-sm">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mx-auto mt-8 flex w-full max-w-sm flex-col items-center gap-6 text-center">
      <font-awesome-icon
        icon="fa-sharp-duotone fa-solid fa-spinner-third"
        spin
        class="h-8 w-8 text-muted-foreground"
        aria-hidden="true"
      />
      <h1 class="text-xl font-semibold">{{ t("msg.redirecting.title") }}</h1>
      <p class="text-sm text-muted-foreground">
        {{
          waitingForGrant
            ? t("msg.redirecting.waiting", { host: targetHost })
            : t("msg.redirecting.message", { host: targetHost })
        }}
      </p>

      <button
        type="button"
        @click="cancel"
        :class="globals.btnClass(theme, highAccessibility)"
        class="flex w-full items-center justify-center rounded-md px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm"
      >
        {{ t("msg.redirecting.cancel") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import WordmarkLogo from "../components/WordmarkLogo.vue";
import globals from "../globals";
import { useAuthStore } from "../pinia/auth";
import { useThemeStore } from "../pinia/theme";
import { isNativePlatform } from "../capacitor/platform";
import { getAclStatus } from "../shared/services/aclStatus";
import { REFERRER_QUERY_KEY, REDIRECT_TARGET_QUERY_KEY } from "../utils/useRedirectUrl";
import { isValidHttpUrl, selectReferrerMatch } from "../utils/urlHelpers";
import { useStatusPolling } from "../utils/useStatusPolling";

// Ceiling on the hold here, counted from mount. A knoc overrides it with
// MaxRedirectWaitMs; 0 never waits. When it elapses we navigate regardless.
const DEFAULT_MAX_REDIRECT_WAIT_MS = 2000;

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const themeStore = useThemeStore();
themeStore.fetchTheme();

const theme = computed(() => themeStore.accent);
const highAccessibility = computed(() => themeStore.highAccessibility);

const referrer = (route.query[REFERRER_QUERY_KEY] as string) || "";
const autoBrowseTarget = (route.query[REDIRECT_TARGET_QUERY_KEY] as string) || "";
const redirectTarget = referrer || autoBrowseTarget;

const targetHost = computed(() => {
  try {
    return new URL(redirectTarget).host;
  } catch {
    return redirectTarget;
  }
});

const authReady = ref(false);
const fired = ref(false);
let timeoutId: ReturnType<typeof setTimeout> | null = null;
let propagationTimeoutId: ReturnType<typeof setTimeout> | null = null;

const isGrantLive = (status: number): boolean => {
  const s = getAclStatus(status);
  return s?.grantIssued === true && s.unstable === false;
};

const matchingAcl = computed(() => {
  if (!authReady.value) return null;
  if (!isValidHttpUrl(redirectTarget)) return null;
  const acls = authStore.user?.ACLs ?? [];
  return selectReferrerMatch(redirectTarget, acls, {
    eligible: (acl) => Boolean(acl.InstantReferrerRedirect || acl.AutoBrowseUsers),
    preferLive: (acl) => isGrantLive(acl.Status),
  });
});

const grantReady = computed(() => {
  const acl = matchingAcl.value;
  if (!acl) return false;
  return isGrantLive(acl.Status);
});

const maxWaitMs = computed(() => matchingAcl.value?.MaxRedirectWaitMs ?? DEFAULT_MAX_REDIRECT_WAIT_MS);
// Only claim to be waiting once the status is in. Before that matchingAcl is
// null, so a knoc whose grant is already live would show "waiting for access".
const waitingForGrant = computed(() => authReady.value && !grantReady.value && maxWaitMs.value > 0);

// Mount time, so the propagation delay can be clamped to what is left of the
// budget instead of being added on top of it.
let waitStartedAt = 0;
const remainingWaitMs = (): number => Math.max(0, maxWaitMs.value - (Date.now() - waitStartedAt));

const isUnstable = computed(() => !grantReady.value);
useStatusPolling({
  fetchStatus: async () => {
    await authStore.fetchStatus({ force: true });
  },
  hasUnstableAcls: isUnstable,
  burstPoll: { intervalMs: 200, durationMs: 5_000 },
});

function scheduleRedirectAfterPropagationDelay() {
  if (fired.value || propagationTimeoutId !== null) return;
  if (authStore.isRedirectCancelled(redirectTarget)) {
    bailToDashboard();
    return;
  }
  // The agent's propagation delay still applies once access is live, but it
  // may not push the user past the knoc's ceiling.
  const delay = Math.min(matchingAcl.value?.PropagationDelayMs ?? 0, remainingWaitMs());
  if (delay <= 0) {
    fireRedirect();
    return;
  }
  propagationTimeoutId = setTimeout(() => {
    fireRedirect();
  }, delay);
}

function fireRedirect() {
  if (fired.value) return;
  if (authStore.isRedirectCancelled(redirectTarget)) {
    bailToDashboard();
    return;
  }
  fired.value = true;
  clearAllTimeouts();
  window.location.assign(redirectTarget);
}

function clearAllTimeouts() {
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  if (propagationTimeoutId !== null) {
    clearTimeout(propagationTimeoutId);
    propagationTimeoutId = null;
  }
}

function bailToDashboard() {
  clearAllTimeouts();
  router.replace({ path: "/home", query: {} });
}

function bailToLogin() {
  clearAllTimeouts();
  router.replace({ name: "Login" });
}

function cancel() {
  authStore.markRedirectCancelled(redirectTarget);
  bailToDashboard();
}

onMounted(async () => {
  waitStartedAt = Date.now();

  if (isNativePlatform()) {
    bailToDashboard();
    return;
  }

  // Bootstrap auth: the route guard can't verify the session on a fresh
  // SAML login because sessionStorage is empty. Fetch it directly.
  try {
    await authStore.fetchStatus({ force: true });
  } catch {
    bailToLogin();
    return;
  }
  authReady.value = true;

  if (!isValidHttpUrl(redirectTarget) || !matchingAcl.value) {
    bailToDashboard();
    return;
  }

  if (authStore.isRedirectCancelled(redirectTarget)) {
    bailToDashboard();
    return;
  }

  if (grantReady.value) {
    scheduleRedirectAfterPropagationDelay();
    return;
  }

  // Charge the fetch above against the budget. Scheduling the full ceiling here
  // would spend it on top of the fetch, roughly doubling a 500ms ceiling.
  const budgetLeft = remainingWaitMs();
  if (budgetLeft <= 0) {
    // No 0ms timer: it would flash the wait copy on a knoc set never to wait.
    fireRedirect();
    return;
  }

  timeoutId = setTimeout(() => {
    // Budget spent. Go regardless: the ceiling is the promise made to the user.
    fireRedirect();
  }, budgetLeft);
});

watch(grantReady, (ready) => {
  if (ready) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    scheduleRedirectAfterPropagationDelay();
  }
});

onBeforeUnmount(() => {
  clearAllTimeouts();
});
</script>
